/**
 * Client-side image compression/resizing.
 *
 * Real-estate photos straight off a phone are often 3–8 MB each. Downsizing to
 * a web-friendly resolution and re-encoding to JPEG before upload dramatically
 * cuts payload size and upload time, which keeps uploads fast and well under
 * the proxy body-size limit. Every code path falls back to the original file on
 * failure, so compression can never block an upload.
 */

const DEFAULT_OPTIONS = {
  // Longest edge, in pixels. 1920px is plenty for web listing photos.
  maxDimension: 1920,
  // JPEG quality (0–1).
  quality: 0.8,
  // Files already smaller than this are left untouched.
  skipBelowBytes: 512 * 1024,
};

/**
 * Compress/resize a single image file. Returns a new JPEG File, or the original
 * file unchanged when compression is skipped or fails.
 *
 * @param {File} file - Source image file
 * @param {object} [options] - Override DEFAULT_OPTIONS
 * @returns {Promise<File>}
 */
export async function compressImage(file, options = {}) {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  // Skip when there's nothing to gain or the environment can't help:
  //  - animated GIFs would be flattened to a single frame by canvas
  //  - small files aren't worth re-encoding
  //  - jsdom/older browsers lack createImageBitmap or a usable canvas
  if (
    !file ||
    file.type === 'image/gif' ||
    file.size <= opts.skipBelowBytes ||
    typeof createImageBitmap !== 'function' ||
    typeof document === 'undefined'
  ) {
    return file;
  }

  let bitmap;
  try {
    // `imageOrientation: 'from-image'` applies EXIF rotation so portrait
    // phone photos don't come out sideways.
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });

    const { width, height } = bitmap;
    const scale = Math.min(1, opts.maxDimension / Math.max(width, height));
    const targetW = Math.max(1, Math.round(width * scale));
    const targetH = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;

    ctx.drawImage(bitmap, 0, 0, targetW, targetH);

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', opts.quality)
    );

    // toBlob may return null, or produce a larger file for already-optimized
    // images — keep the original in those cases.
    if (!blob || blob.size >= file.size) return file;

    const newName = file.name.replace(/\.[^./\\]+$/, '') + '.jpg';
    return new File([blob], newName, {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });
  } catch (err) {
    console.warn('Image compression failed; uploading original file.', err);
    return file;
  } finally {
    if (bitmap && typeof bitmap.close === 'function') bitmap.close();
  }
}

/**
 * Compress an array/FileList of images, preserving order.
 *
 * @param {FileList|File[]} files
 * @param {object} [options]
 * @returns {Promise<File[]>}
 */
export async function compressImages(files, options = {}) {
  return Promise.all(Array.from(files).map((file) => compressImage(file, options)));
}
